import { LitElement, type PropertyValues, css, html, nothing } from "lit";

import { CodeEditorFabric, type CodeEditorSettings } from "@whilecat/core/editors/code-editor-fabric.js";
import type { CodeEditorView } from "@whilecat/core/editors/code-editor-view.js";
import { areNotEqual } from "@whilecat/core/utils/objects.js";
import { requireNotNull } from "@whilecat/core/utils/validation.js";
import { customElement, property } from "lit/decorators.js";

import "@whilecat/core/editors/buttons/copy-button.ts";
import "@whilecat/core/editors/buttons/reset-button.ts";
import "@whilecat/core/editors/buttons/show-answer-button.ts";
import "@whilecat/core/editors/buttons/view-button.ts";
import { noop } from "lodash";

@customElement("code-area")
export class CodeArea extends LitElement {
    static shadowRootOptions = { mode: "open" as const, delegatesFocus: true };

    static styles = css`
      :host {
        display: flex;
        position: relative;
        align-items: center;
        background-color: var(--whilecat-code-editor-background-color);
        border-radius: 12px;
      }

      #container {
        position: relative;
        width: 100%;
      }

      .cm-editor, .cm-scroller {
        border-radius: 10px;
      }

      .actionButtonGroup {
        position: absolute;
        right: 15px;
        top: 5px;
        z-index: var(--sl-z-index-dialog);
        display: flex;
        justify-content: space-between;
      }

      .actionButtonGroup * {
          padding-left: 6px;
      }

      sl-copy-button.actionButtonGroup::part(tooltip__base) {
        z-index: var(--sl-z-index-tooltip);
      }

      .annotation, .annotation .ͼq {
        color: #d0c658;
      }

      .readonly-code {
        opacity: 0.5;
      }

      .under-stroked {
        text-decoration-line: underline;
        text-decoration-style: wavy;
        text-decoration-thickness: 0.04rem;
        text-underline-offset: 0.2rem;
        text-decoration-color: var(--sl-color-cyan-600);
      }

      .cm * {
        font-family: var(--whilecat-default-font-mono);
      }
    `;

    @property({ type: Object, hasChanged: areNotEqual }) settings?: CodeEditorSettings;

    @property({ type: Boolean })
    private showAnswerButtonEnabled = false;

    private editorView?: CodeEditorView;

    static html(settings: CodeEditorSettings) {
        return html`<code-area .settings="${settings}"></code-area>`;
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);

        const container = this.shadowRoot!.getElementById("container");

        this.editorView = new CodeEditorFabric(this.settings!, container!).build();

        this.setupReadOnlyCodeHoverHandlers();

        if (this.settings?.focus) this.editorView.focus();
    }

    private editor(): CodeEditorView {
        return requireNotNull(this.editorView);
    }

    blur() {
        this.editor().blur();
    }

    enableShowAnswerButton() {
        this.showAnswerButtonEnabled = true;
    }

    /**
     * Setups listeners to mouse enter event to make fully visible readonly lines
     * when any of them is hovered.
     */
    setupReadOnlyCodeHoverHandlers() {
        const elements = this.shadowRoot!.querySelectorAll(".readonly-code") as NodeListOf<HTMLElement>;

        elements.forEach(element => {
            element.addEventListener("mouseenter", () => {
                elements.forEach(el => (el.style.opacity = "1"));
            });

            element.addEventListener("mouseleave", () => {
                elements.forEach(el => (el.style.opacity = ""));
            });
        });
    }

    setEditable(editable: boolean) {
        this.editor().setEditable(editable);
    }

    getMarkedLines() {
        return this.editor().getMarkedLines();
    }

    markLines(lines: Array<number>) {
        this.editor().markLines(lines);
    }

    getCode() {
        return this.editor().getCurrentCode();
    }

    setCode(code: string) {
        this.editor().setCode(code);
    }

    disable() {
        this.editor().disable();
        const elements = this.shadowRoot!.querySelectorAll("#line-checkbox");
        elements.forEach((element: Element) => {
            // FIXME
            // @ts-ignore
            element.disabled = true;
        });
    }

    private getActionButtons() {
        const disabled = !this.settings!.editable || this.settings!.disabled;

        return html`
          <div class="actionButtonGroup">
            <show-answer-button .visible="${this.settings!.showAnswerButtonVisible}"
                                .showAnswerCallBack="${this.settings!.showAnswerButtonCallBack || noop}"
                                ?disabled="${!this.showAnswerButtonEnabled || disabled}"
            ></show-answer-button>
            <reset-button .visible="${this.settings!.editable && !this.settings!.disabled}"
                          .resetButtonCallBack="${this.settings!.resetButtonCallBack || noop}"
                          ?disabled="${disabled}"
            ></reset-button>
            <copy-button .codeArea="${this}"></copy-button>
          </div>
        `;
    }

    render() {
        return html`
          <div id="container">
          </div>
          ${this.getActionButtons()}
        `;
    }
}
